import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { questionsAPI } from "../api/index.js";
import SEO from "../components/SEO.jsx";

const G = {
  surface: "#111111", border: "#1E1E1E", text: "#E4E4DC",
  muted: "#666", accent: "#00C896", purple: "#7C6FFF", danger: "#FF6B6B",
  font: "'Sora', sans-serif", mono: "'DM Mono', monospace",
};

const PAGE_SIZE = 20;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const page = Number(searchParams.get("page")) || 1;
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!q.trim()) { setQuestions([]); setTotal(0); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await questionsAPI.search({ q, page, limit: PAGE_SIZE });
      setQuestions(res.data.questions || []);
      setTotal(res.data.total || 0);
    } catch {
      setQuestions([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);

  const setPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(p));
    setSearchParams(next);
  };

  return (
    <div style={{ maxWidth:780, margin:"0 auto", padding:"28px 16px", fontFamily:G.font }}>
      <SEO
        title={q ? `Search results for "${q}"` : "Search"}
        description={q ? `Search results for "${q}" on askFOGRA.` : "Search questions on askFOGRA."}
        url={`/search${q ? `?q=${encodeURIComponent(q)}` : ""}`}
      />
      <Link to="/feed" style={{ textDecoration:"none", color:G.muted, fontSize:13, fontFamily:G.mono }}>← Back to Feed</Link>
      <h1 style={{ fontSize:24, fontWeight:700, color:G.text, margin:"18px 0 6px" }}>
        {q ? `Search results for "${q}"` : "Search"}
      </h1>
      <p style={{ color:G.muted, fontSize:14, marginBottom:24 }}>
        {loading ? "Searching..." : `${total} result${total !== 1 ? "s" : ""}`}
      </p>

      {loading ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:G.muted }}>Loading...</div>
      ) : questions.length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#333" }}>
          {q ? `No results found for "${q}"` : "Type a search term to get started."}
        </div>
      ) : (
        questions.map(qq => (
          <Link key={qq.id} to={`/q/${qq.slug}`} style={{ textDecoration:"none" }}>
            <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:10, padding:"14px 18px", marginBottom:8, transition:"border-color 0.15s" }}
              onMouseOver={e => e.currentTarget.style.borderColor="#2A2A2A"}
              onMouseOut={e => e.currentTarget.style.borderColor=G.border}>
              {qq.topic_name && (
                <span style={{ background:(qq.topic_color||G.purple)+"18", border:`1px solid ${qq.topic_color||G.purple}44`, color:qq.topic_color||G.purple, fontSize:10, padding:"2px 10px", borderRadius:20, fontFamily:G.mono, display:"inline-block", marginBottom:8 }}>{qq.topic_name}</span>
              )}
              <div style={{ fontSize:14, fontWeight:600, color:G.text, marginBottom:6, lineHeight:1.4 }}>{qq.title}</div>
              <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
                <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>▲ {qq.vote_score ?? 0}</span>
                <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>💬 {qq.answer_count ?? 0}</span>
                <span style={{ fontSize:11, color:"#3A3A3A", fontFamily:G.mono }}>👁 {qq.views ?? 0}</span>
                <span style={{ fontSize:11, color:"#3A3A3A", fontFamily:G.mono }}>{qq.created_at ? new Date(qq.created_at).toLocaleDateString() : ""}</span>
              </div>
            </div>
          </Link>
        ))
      )}

      {total > PAGE_SIZE && (
        <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:20 }}>
          <button disabled={page===1} onClick={() => setPage(page-1)}
            style={{ background:"#1A1A1A", color:G.muted, border:"1px solid #2A2A2A", borderRadius:24, padding:"7px 18px", fontFamily:G.mono, fontSize:12, cursor:page===1?"default":"pointer", opacity:page===1?0.4:1 }}>← Prev</button>
          <span style={{ padding:"5px 12px", fontFamily:G.mono, fontSize:12, color:G.muted }}>Page {page}</span>
          <button disabled={page*PAGE_SIZE>=total} onClick={() => setPage(page+1)}
            style={{ background:"#1A1A1A", color:G.muted, border:"1px solid #2A2A2A", borderRadius:24, padding:"7px 18px", fontFamily:G.mono, fontSize:12, cursor:page*PAGE_SIZE>=total?"default":"pointer", opacity:page*PAGE_SIZE>=total?0.4:1 }}>Next →</button>
        </div>
      )}
    </div>
  );
}
